import FlexSearch from 'flexsearch';
import Mustache from 'mustache';
import cloneDeep from 'lodash/cloneDeep';
import dataLayer from './data-layer';
import debounce from 'lodash.debounce';

import { keyboardNavigable, nonRollingControlMode } from '../modules/mixins/keyboard-navigable';

class FreeTextSearch {
  /**
   * Ajax endpoint where we can get fresh data
   *
   * @type {string}
   * @private
   */
  #searchItemsAjaxEndPoint = '/stage-ajax/free-text-search-items/';

  /**
   * Session storage name where data from server will
   * be stored. If modifying data structure, please
   * add one number more to vX
   *
   * @type {string}
   * @private
   */
  #sessionStorageName = 'freeTextSearchSearchIndexStore_v1';

  /**
   * searchValues contains all items that is searchable
   * Basically value will be assigned from server or from
   * sessionStorage
   *
   * @type {object[]}
   * @private
   */
  #searchValues = [];

  /**
   * Free text search container element
   *
   * @type {HTMLElement|null}
   * @private
   */
  #element = null;

  /**
   * Free text search input. Filled in constructor
   *
   * @type {HTMLElement|null}
   * @private
   */
  #freeTextSearchInput = null;

  /**
   * Search suggestion container element
   *
   * @type {HTMLElement|null}
   * @private
   */
  #searchSuggestionContainer = null;

  /**
   * Search synonyms array of objects
   *
   * @type {object[]}
   */
  #searchSynonyms = [];

  /**
   * Site language
   */
  #language = 'fi';

  /**
   * Override results mustache
   */
  #localResultsPartial = false;

  /**
   * regex of a valic license plate
   */
  validLicense = /^([a-zåäö]{1,4}-?\d{1,6})$/i;

  /**
   * Mapping for legacy params, for translating them to new param field names.
   *
   * @type {Object.<string, string>}
   * @private
   */
  #legacyParamMapping = {
    'fueltype[]': 'fuel[]',
    fueltype: 'fuel[]',

    'bodytype[]': 'body_type[]',
    bodytype: 'body_type[]',

    'drivetype[]': 'drive[]',
    drivetype: 'drive[]',
  };

  /**
   * Constructor of FreeTextSearch
   * It will load items either from sessionStorage
   * or from server if session storage does not exists
   * @param {HTMLElement} element - Free text search container element
   */
  constructor(element) {
    this.#element = element;
    this.#freeTextSearchInput = this.#element.querySelector('.js-free-text-search-input') || null;
    this.#searchSuggestionContainer = this.#element.querySelector('.js-search-suggestion-container') || null;

    this.#localResultsPartial = document.querySelector('#flex-search-suggestions')?.innerHTML;

    this.#language = document.documentElement.lang || 'fi';

    this.searchIndex = FlexSearch.Index({
      profile: 'score',
      tokenize: 'forward',
      charset: 'latin:advanced',
    });

    // debounce this so we don't send a constant bombardment of events
    this.analyticsPush = debounce(this.analyticsPush, 3_000, {
      leading: false,
      trailing: true,
    });

    this.bindKeyboardNavitable(element);

    // Bind event
    this.#bindEvents();

    // Get synonyms from window-object
    try {
      const synonyms = JSON.parse(window.searchSynonyms);
      synonyms.forEach(word => {
        word.synonym_word.split(',').map(synonym => {
          this.#searchSynonyms.push({
            synonym_word: synonym.trim().toLowerCase(),
            meaning_word: word.meaning_word.toLowerCase(),
          });
        });
      });
    } catch (e) {
      console.warn('Could not parse synonyms!', e);
    }

    // Load items and then add items to FlexSearch index
    this.#loadItems().then(data => {
      if (data) {
        this.#indexItems(data);
      } else {
        console.error('Could not index data to FlexSearch. Data was invalid.');
      }
    });
  }

  /**
   * If we want to look directly for a vehicle result based on licenseplate
   * @param {string} license vehicles license number to look for
   * @returns {boolean|object} vehicle result render data or false.
   */
  async findVehicleByLicense(license) {
    const url = `/stage-ajax/getVehicles?license=${license}`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }

      const json = await response.json();
      if (Array.isArray(json.items) && json.items.length === 1) {
        // Found exactly one result.
        const [vehicle] = json.items;
        if (vehicle?.license?.localeCompare(license, undefined, { sensitivity: 'base' }) === 0) {
          // It appears to be an object containing the same license we were looking for, great success!
          return {
            lisenceLink: true,
            link: vehicle.href,
            value: [vehicle.make, vehicle.model, vehicle.year_model, vehicle.license].join(' '),
            rawJSON: { href: vehicle.href },
          };
        }
      }
    } catch (error) {
      console.error(error.message);
    }
    return false;
  }

  /**
   * Do FlexSearch string search
   *
   * @param {string} searchString - Search string
   * @param {number} resultAmount - Number of results
   * @returns {object[]}
   * @async
   */
  async #doFreeTextSearch(searchString, resultAmount = 5) {
    if (this.validLicense.test(searchString.trim())) {
      const licenseResult = await this.findVehicleByLicense(searchString);
      console.log('found valid license!', searchString, licenseResult);
      if (licenseResult) return cloneDeep([licenseResult]);
    }

    // Execute search function in FlexSearch
    const resultIds = await this.searchIndex.search(searchString, {
      limit: resultAmount,
      suggest: false,
    });

    const returnValue = [];

    for (const id of resultIds) {
      // FlexSearch returns only IDs, so we need to
      // push results from this.#searchValues by ID
      const result = this.#searchValues[id];

      // Parse GET-parameters
      const getParameters = new URLSearchParams(new URL(result.link).search);
      const selectedParams = {};

      // Add GET-parameters according to result
      for (const [key, value] of getParameters) {
        selectedParams[key] = value;
      }

      // Check if result is already in the return value
      if (!returnValue.length || !returnValue.some(item => item.value?.toLowerCase().includes(result.value?.toLowerCase()))) {
        returnValue.push({
          ...result,
          rawJSON: result.raw ? JSON.stringify(result.raw) : JSON.stringify(selectedParams),
          selectedParams,
        });
      }
    }

    this.analyticsPush(searchString);
    // Make deep clone of return values
    // Weird things happened if returnValue
    // is not deep cloned.
    return cloneDeep(returnValue);
  }

  /**
   * Push search event to analytics
   * @param {string} searchString - Search string
   */
  analyticsPush(searchString) {
    const event = {
      event: 'free_text_search',
      eventCategory: 'FreeSearch - frontpage',
      eventAction: 'Search',
      eventLabel: searchString,
      nonInteraction: false,
    };
    // console.log(event); // TODO: remove this logging
    dataLayer.push(event);
  }

  /**
   * Bind events to free text field
   */
  #bindEvents() {
    // Fetch results on input
    this.#freeTextSearchInput.addEventListener('input', async e => {
      if (this.#freeTextSearchInput.value.length >= 2) {
        const searchString = this.#checkAndReplaceSynonyms(this.#freeTextSearchInput.value);
        const results = await this.#doFreeTextSearch(searchString);
        this.#renderResults(results);
      } else {
        this.#searchSuggestionContainer.innerHTML = '';
        this.#hideSuggestions();
      }
    });

    // Listen event when user clicks result from free text field results area
    this.#freeTextSearchInput.addEventListener('click', async e => {
      if (this.#searchSuggestionContainer.children.length) {
        this.#showSuggestions();
      } else if (this.#freeTextSearchInput.value.length >= 2) {
        const searchString = this.#checkAndReplaceSynonyms(this.#freeTextSearchInput.value);
        const results = await this.#doFreeTextSearch(searchString);
        this.#renderResults(results);
      }
    });

    // Hide suggestions when user clicks outside of the free text field
    window.addEventListener('click', event => {
      if (!event.target.closest('.js-free-text-search')) {
        this.#hideSuggestions();
      }
    });

    // Hide suggestions when user presses escape
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        this.#hideSuggestions();
      }
    });
  }

  /**
   * Find synonyms in search string and replace it with meaning word
   *
   * @param {string} searchString - Current search string
   * @returns {string} Replaced search string or original
   * @private
   */
  #checkAndReplaceSynonyms(searchString) {
    if (searchString.length) {
      const replacedSearchString = searchString
        .split(' ')
        .map(word => {
          // Do not match empty string
          if (!word.length) {
            return word;
          }

          const foundWord = this.#searchSynonyms.find(o => o.synonym_word === word.toLowerCase());
          return foundWord ? foundWord.meaning_word : word;
        })
        .join(' ');
      return replacedSearchString;
    }
    return searchString;
  }

  /**
   * Add items to FlexSearch index
   *
   * @param {object[]} indexData - Data to add FlexSearch index
   * @private
   */
  #indexItems(indexData) {
    this.#searchValues = indexData;
    for (const item in indexData) {
      indexData[item].id = item;
      this.searchIndex.add(indexData[item].id, indexData[item].keywords);
    }
  }

  /**
   * Show suggestion list
   */
  #showSuggestions() {
    this.#element.classList.add('show-suggestions');

    this.setFocusedElement(this.focusableElements[0]);
  }

  /**
   * Hide suggestion list
   */
  #hideSuggestions() {
    this.#element.classList.remove('show-suggestions');
  }

  /**
   * Load items either from sessionStorage or server
   * If sessionStorage does not exists, load fresh data
   * from server.
   *
   * @returns {object[]|null} - Data from server or from sessionStorage or null if server request fails
   * @private
   * @async
   */
  async #loadItems() {
    let dataFromSessionStorage = null;
    let languageFromSessionStorage = null;

    if (window.sessionStorage?.getItem(this.#sessionStorageName) && languageFromSessionStorage === this.#language) {
      // Try parse session storage
      try {
        // If couldn't parse, keep dataFromSessionStorage null to
        // indicate that we have to get fresh data from server
        dataFromSessionStorage = JSON.parse(window.sessionStorage.getItem(this.#sessionStorageName));
        languageFromSessionStorage = window.sessionStorage.getItem(this.#sessionStorageName + '_lang');

        return dataFromSessionStorage;
      } catch (e) {
        console.log('JSON data invalid in sessionStorage, try get fresh data from server');
      }
    }

    // sessionStorage data does not exists or data is corrupted
    // Get fresh data from server
    if (!dataFromSessionStorage || languageFromSessionStorage !== this.#language) {
      try {
        const response = await fetch(this.#searchItemsAjaxEndPoint + `?cm_lang=${this.#language}`);
        const responseJson = await response.json();
        sessionStorage.setItem(this.#sessionStorageName, JSON.stringify(responseJson));
        sessionStorage.setItem(this.#sessionStorageName + '_lang', this.#language);
        return responseJson;
      } catch (error) {
        console.log('Could not get data from server correctly.', error);
      }
    }
  }

  /**
   * @typedef {object} ResultItem
   * @property {string} keywords - Keyword that is rendered as a search item. Mandatory.
   * @property {string} link - Link that is rendered to link element. Mandatory.
   * @property {string} [results] - Results count that is rendered to element. Optional.
   */

  /**
   * Render free text results element with found results
   * Is callable from somewhere else.
   *
   * @param {ResultItem[]} results - Results array of objects
   */
  #renderResults(results) {
    this.#patchLinks(results);
    const html = Mustache.render(this.#searchResultTemplate(), { results });
    this.#searchSuggestionContainer.innerHTML = html;

    if (results.length) {
      this.#updateFocusableElement();
      this.#showSuggestions();
    } else {
      this.#hideSuggestions();
    }
  }

  /**
   * Patches the links in an array of result objects by mapping legacy query parameters
   * to new parameter names based on the `#newParamMapping` property.
   * @see {@link https://crasman.atlassian.net/browse/KAYTCONT-753|Ticket describing the need for this method}
   *
   * @param {ResultItem[]} results An array of result objects to patch.
   * @returns {void} Modifies the `results` array in place.
   */
  #patchLinks(results) {
    for (const result of results) {
      /* we need to prevent this from running on license plate results, as they are not search results
       * @see https://crasman.atlassian.net/browse/KAYTCONT-758
       */
      if (result?.link && !result?.lisenceLink) {
        const url = new URL(result.link);
        const { searchParams } = url;
        const newParams = new URLSearchParams();

        searchParams.forEach((value, key) => {
          if (this.#legacyParamMapping[key]) {
            // Add params with mapped key, same value, if needed
            newParams.append(this.#legacyParamMapping[key], value);
          } else {
            // Add other params as is
            newParams.append(key, value);
          }
        });

        url.search = newParams.toString();
        result.link = url.toString();
      }
    }
  }

  /**
   * Search result template
   * @returns {string} template
   * @private
   */
  #searchResultTemplate() {
    if (this.#localResultsPartial) return this.#localResultsPartial;
    // Keep ' markers around data-searchdata='' intact.. otherwise you brake the JSON enconding
    return `{{#results}}
      <a href='{{link}}' class="frontpage-hero-search__search-suggestion" tabindex="-1" data-searchdata='{{{data}}}'>
        {{^value}}{{{keywords}}}{{/value}}
        {{#value}}{{{value}}}{{/value}}
      </a>
    {{/results}}`;
  }

  /**
   * Update focusable element list for the keyboard navigation after each search.
   *
   * @private
   */
  #updateFocusableElement() {
    this.focusableElements = Array.from(this.#element.querySelectorAll('input, .js-search-suggestion-container a'));
  }

  static init() {
    const element = document.querySelectorAll('.js-free-text-search');

    element.forEach(element => {
      new FreeTextSearch(element);
    });
  }
}

// Assign mixins to make element keyboard navigable
Object.assign(FreeTextSearch.prototype, keyboardNavigable, nonRollingControlMode);

export default FreeTextSearch;
